
import React from 'react';
import { Button } from '@/components/ui/button';
import { List, CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { hu } from 'date-fns/locale';
import { ViewMode } from '@/lib/types';

interface ViewToggleBarProps {
  viewMode: ViewMode;
  toggleViewMode: () => void;
  currentDate: Date;
  days?: Date[];
  showToggleButton?: boolean;
}

const ViewToggleBar: React.FC<ViewToggleBarProps> = ({ 
  viewMode, 
  toggleViewMode,
  currentDate,
  days = [],
  showToggleButton = true
}) => {
  // Show date range if days are provided
  const getDateRangeDisplay = () => {
    if (!days || days.length < 2) {
      return format(currentDate, 'yyyy. MMMM', { locale: hu });
    }
    
    const firstDay = days[0];
    const lastDay = days[days.length - 1];
    
    // If both dates are in the same month and year
    if (firstDay.getMonth() === lastDay.getMonth() && 
        firstDay.getFullYear() === lastDay.getFullYear()) {
      const monthYear = format(firstDay, 'yyyy. MMMM', { locale: hu });
      // Updated Hungarian format: year. month day-day.
      return `${monthYear} ${format(firstDay, 'd', { locale: hu })}-${format(lastDay, 'd', { locale: hu })}.`;
    }
    
    // If dates are in different months but same year
    if (firstDay.getFullYear() === lastDay.getFullYear()) {
      return `${format(firstDay, 'yyyy. MMMM d', { locale: hu })}-${format(lastDay, 'MMMM d', { locale: hu })}.`;
    }
    
    // If dates are in different years
    return `${format(firstDay, 'yyyy. MMMM d', { locale: hu })}. - ${format(lastDay, 'yyyy. MMMM d', { locale: hu })}.`;
  };

  return (
    <div className="flex items-center justify-between border-b border-medihub-primary/10 p-4 bg-gradient-to-r from-medihub-softBg to-white shadow-sm rounded-t-xl">
      <div className="flex items-center gap-2">
        {showToggleButton && (
          <Button
            variant="outline"
            size="sm"
            onClick={toggleViewMode}
            className="flex items-center bg-white border-medihub-primary/20 hover:bg-medihub-lightPurple hover:text-medihub-primary transition-all duration-300 shadow-sm"
          >
            {viewMode === 'calendar' ? <List className="h-4 w-4 mr-2" /> : <CalendarIcon className="h-4 w-4 mr-2" />}
            {viewMode === 'calendar' ? 'Lista nézet' : 'Naptár nézet'}
          </Button>
        )}
      </div>
      
      {viewMode === 'calendar' && (
        <div className="text-xl font-medium text-medihub-primary bg-medihub-lightPurple/30 px-4 py-1 rounded-full shadow-sm">
          {getDateRangeDisplay()}
        </div>
      )}
    </div>
  );
};

export default ViewToggleBar;
